/**
 * Enhanced Localization Handler
 * Handles language persistence and proper URL routing for multilingual Shopify stores
 */

// Immediate URL cleanup for English pages with /en/ prefix
(function immediateCleanup() {
  const currentUrl = window.location.pathname;
  const documentLang = document.documentElement.lang;
  
  // If we're on an English page with /en/ prefix, redirect immediately
  if (documentLang === 'en' && currentUrl.startsWith('/en/')) {
    const cleanUrl = currentUrl.replace(/^\/en\//, '/');
    const newUrl = cleanUrl + window.location.search + window.location.hash;
    
    console.log('Immediate English URL cleanup:', {
      from: currentUrl,
      to: cleanUrl,
      documentLang: documentLang
    });
    
    // Use replace to avoid adding to browser history
    window.location.replace(newUrl);
    return;
  }
})();

class EnhancedLocalization {
  constructor() {
    this.currentLanguage = this.getCurrentLanguageFromDocument();
    this.init();
  }

  getCurrentLanguageFromDocument() {
    // Try to get language from document lang attribute first
    const docLang = document.documentElement.lang;
    if (docLang) {
      return docLang;
    }
    
    // Fallback to URL detection
    const urlMatch = window.location.pathname.match(/^\/([a-z]{2}(-[A-Z]{2})?)\//);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    // Default to English
    return 'en';
  }

  init() {
    // Store current language in session storage
    sessionStorage.setItem('selectedLanguage', this.currentLanguage);
    
    console.log('Enhanced Localization initialized:', {
      currentLanguage: this.currentLanguage,
      storedLanguage: sessionStorage.getItem('selectedLanguage'),
      documentLang: document.documentElement.lang,
      currentUrl: window.location.pathname
    });
    
    // Check and fix English URLs with /en/ prefix
    this.cleanupEnglishUrls();
    
    // Override any existing Shopify localization behavior
    this.overrideShopifyLocalization();
    
    // Set up language switching
    this.setupLanguageSwitching();
    
    // Check for stored language preference
    this.checkStoredLanguage();
    
    // Set up navigation cleanup
    this.setupNavigationCleanup();
  }

  cleanupEnglishUrls() {
    const currentUrl = window.location.pathname;
    const currentLanguage = this.currentLanguage;
    
    // If we're on an English page with /en/ prefix, redirect to clean URL
    if (currentLanguage === 'en' && currentUrl.startsWith('/en/')) {
      const cleanUrl = currentUrl.replace(/^\/en\//, '/');
      const newUrl = cleanUrl + window.location.search + window.location.hash;
      
      console.log('Cleaning up English URL:', {
        from: currentUrl,
        to: cleanUrl,
        fullNewUrl: newUrl
      });
      
      // Use replace to avoid adding to browser history
      window.location.replace(newUrl);
      return;
    }
  }

  overrideShopifyLocalization() {
    // Disable any existing LocalizationForm custom elements
    if (window.customElements && window.customElements.get('localization-form')) {
      console.log('Overriding existing localization-form custom element');
    }
    
    // Override any existing localization form submissions
    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action*="localization"]') || 
          e.target.closest('localization-form')) {
        console.log('Intercepted localization form submission, preventing default');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    }, true);
    
    // Override any clicks on localization-related elements
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-language-code], [data-value]');
      if (target && (target.dataset.languageCode || target.dataset.value)) {
        const languageCode = target.dataset.languageCode || target.dataset.value;
        if (languageCode && languageCode.match(/^[a-z]{2}(-[A-Z]{2})?$/)) {
          console.log('Intercepted language selection:', languageCode);
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.switchLanguage(languageCode);
          return false;
        }
      }
    }, true);
  }

  setupLanguageSwitching() {
    const languageLinks = document.querySelectorAll('.language-switch-link');
    
    languageLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const targetLanguage = link.dataset.languageCode;
        console.log('Language link clicked:', targetLanguage);
        
        // Prevent any form submission or default behavior
        if (e.target.closest('form')) {
          e.target.closest('form').addEventListener('submit', (formEvent) => {
            formEvent.preventDefault();
            formEvent.stopPropagation();
          });
        }
        
        this.switchLanguage(targetLanguage);
        return false;
      }, true); // Use capture phase to ensure we get the event first
    });
    
    // Also prevent any localization form submissions
    const localizationForms = document.querySelectorAll('form[action*="localization"]');
    localizationForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Prevented localization form submission');
        return false;
      });
    });
  }

  switchLanguage(targetLanguage) {
    // Store the selected language in session storage
    sessionStorage.setItem('selectedLanguage', targetLanguage);
    
    const currentUrl = window.location.pathname;
    const currentSearch = window.location.search;
    const currentHash = window.location.hash;
    
    let newUrl = this.buildLanguageUrl(currentUrl, targetLanguage);
    
    console.log('Language switch:', {
      from: this.currentLanguage,
      to: targetLanguage,
      currentUrl: currentUrl,
      newUrl: newUrl,
      fullNewUrl: newUrl + currentSearch + currentHash
    });
    
    // Redirect to the new URL
    window.location.href = newUrl + currentSearch + currentHash;
  }

  buildLanguageUrl(currentUrl, targetLanguage) {
    console.log('Building URL:', { currentUrl, targetLanguage });
    
    let newUrl;
    
    if (targetLanguage === 'en') {
      // For English, ALWAYS remove any language prefix - never add /en/
      newUrl = this.removeLanguagePrefix(currentUrl);
      console.log('English: removed prefix, result:', newUrl);
      // Ensure we have at least a root path
      if (newUrl === '' || newUrl === '/') {
        newUrl = '/';
      }
    } else if (targetLanguage === 'es') {
      // For Spanish, add /es prefix
      newUrl = this.addLanguagePrefix(currentUrl, 'es');
      console.log('Spanish: added prefix, result:', newUrl);
    } else {
      // For other languages, use standard Shopify format
      newUrl = this.addLanguagePrefix(currentUrl, targetLanguage);
      console.log('Other language: added prefix, result:', newUrl);
    }
    
    // Ensure we don't have double slashes
    newUrl = newUrl.replace(/\/+/g, '/');
    console.log('Final URL after cleanup:', newUrl);
    
    return newUrl;
  }

  removeLanguagePrefix(url) {
    console.log('removeLanguagePrefix input:', url);
    // Remove any existing language prefix (including /en/, /es/, /fr/, etc.)
    if (url.match(/^\/[a-z]{2}(-[A-Z]{2})?\//)) {
      const cleanUrl = url.replace(/^\/[a-z]{2}(-[A-Z]{2})?\//, '/');
      console.log('removeLanguagePrefix: found prefix, cleaned to:', cleanUrl);
      return cleanUrl;
    }
    // If no language prefix found, return as-is
    console.log('removeLanguagePrefix: no prefix found, returning as-is');
    return url;
  }

  addLanguagePrefix(url, language) {
    console.log('addLanguagePrefix input:', { url, language });
    // First remove any existing language prefix
    const cleanUrl = this.removeLanguagePrefix(url);
    
    // Add the new language prefix
    if (cleanUrl === '/' || cleanUrl === '') {
      const result = `/${language}/`;
      console.log('addLanguagePrefix: root path, result:', result);
      return result;
    }
    const result = `/${language}${cleanUrl}`;
    console.log('addLanguagePrefix: non-root path, result:', result);
    return result;
  }

  checkStoredLanguage() {
    const storedLanguage = sessionStorage.getItem('selectedLanguage');
    const currentUrl = window.location.pathname;
    const isOnRootOrEnglish = !currentUrl.startsWith('/es/');

    // Never auto-redirect to /es when user is on main domain (default English).
    // Main domain (e.g. texasart.com) should stay English unless user explicitly chooses Spanish.
    if (storedLanguage === 'es' && isOnRootOrEnglish) {
      return;
    }

    if (storedLanguage && storedLanguage !== this.currentLanguage) {
      const hasAutoSwitched = sessionStorage.getItem('hasAutoSwitched');

      if (!hasAutoSwitched) {
        sessionStorage.setItem('hasAutoSwitched', 'true');

        setTimeout(() => {
          this.switchLanguage(storedLanguage);
        }, 100);
      }
    }
  }

  setupNavigationCleanup() {
    // Clear auto-switch flag when user manually navigates
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('hasAutoSwitched');
    });

    // Also clear on popstate (back/forward navigation)
    window.addEventListener('popstate', () => {
      sessionStorage.removeItem('hasAutoSwitched');
    });
  }

  // Utility method to get current language from URL or document
  static getCurrentLanguage() {
    const urlMatch = window.location.pathname.match(/^\/([a-z]{2}(-[A-Z]{2})?)\//);
    if (urlMatch) {
      return urlMatch[1];
    }
    return document.documentElement.lang || 'en';
  }

  // Utility method to check if current page is in a specific language
  static isCurrentLanguage(language) {
    return this.getCurrentLanguage() === language;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new EnhancedLocalization();
});

// Also initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
  // DOM is still loading, wait for DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedLocalization();
  });
} else {
  // DOM is already loaded, initialize immediately
  new EnhancedLocalization();
}

// Export for use in other scripts if needed
window.EnhancedLocalization = EnhancedLocalization; 